import { useCallback, useEffect, useMemo, useState, useDeferredValue } from 'react';

import { SortingState } from '@tanstack/react-table';
import { BlocksIcon, GoalIcon, LightbulbIcon, PlusIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useTitle } from 'react-use';
import { toast } from 'sonner';

import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { FilterDefinition } from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import useBrains from '@/hooks/useBrains';
import { getFilterValues, useTableFilters } from '@/hooks/useTableFilters';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { setCampaignId } from '@/redux/session/actions';
import { selectAccountId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { CampaignType } from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { EventName, PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import {
  CAMPAIGN_STATUSES,
  getCampaignStatusLabelKey,
} from '../components/analytics/campaignStatusBadge';
import { CampaignsToolbar } from '../components/CampaignsToolbar';
import { EmptyCampaignsPage } from '../components/EmptyCampaignsPage';
import { DeleteCampaignModal } from '../components/modals/DeleteCampaignModal';
import { StopCampaignModal } from '../components/modals/StopCampaignModal';
import { CHANNEL_OPTIONS } from '../components/Tables/CampaignLogsTable/constants';
import { CampaignsTable } from '../components/Tables/CampaignsTable/Table';
import { useCampaigns } from '../hooks/useCampaigns';
import { useCreateCampaign } from '../hooks/useCreateCampaign';
import { clearDraftCampaign } from '../redux/actions';

const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  status: 'status',
  created: 'created',
  subscribers_count: 'subscribers_count',
};

// Matches orchestrator's brain_parent_ids Joi cap.
const MAX_AI_AGENTS_FILTER = 25;

/**
 * Main campaigns listing page
 * Shows all campaigns with create functionality
 */
export const CampaignsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountId = useSelector(selectAccountId);
  const { createCampaign } = useCreateCampaign();
  useTitle(t('pages.campaigns'));

  // State for search filter and sorting
  const [searchFilter, setSearchFilter] = useState<string>('');
  const deferredSearchFilter = useDeferredValue(searchFilter);
  const [sorting, setSorting] = useState<SortingState>([]);

  const sortParam = useMemo(() => {
    if (sorting.length === 0) {
      return undefined;
    }
    const { id, desc } = sorting[0];
    const field = SORT_FIELD_MAP[id];
    if (!field) {
      return undefined;
    }
    return desc ? `-${field}` : field;
  }, [sorting]);

  // State for status + channel filters
  const [openPillId, setOpenPillId] = useState<string | null>(null);
  const {
    filters,
    activeFilterCount,
    setFilter: setFilterRaw,
    removeFilter,
    resetFilters,
  } = useTableFilters({
    // Scoped per account: ai_agent stores brain ids, which are account-specific.
    storageKey: `campaignsFilters:${accountId}`,
  });

  const { brains } = useBrains();

  // 'null' maps to brain_parent_id IS NULL server-side; agent ids are brain_parent_ids.
  const aiAgentOptions = useMemo(() => {
    const options = (brains ?? []).map((brain) => ({ label: brain.name, value: brain.brain_id }));
    return [{ label: t('campaigns.not_assigned'), value: 'null' }, ...options];
  }, [brains, t]);

  // brain_parent_ids is capped at 25 server-side; block over-selection here instead.
  const setFilter = useCallback(
    (key: string, values: string[], exclude?: boolean) => {
      if (key === 'ai_agent' && values.length > MAX_AI_AGENTS_FILTER) {
        toast.error(t('filters.max_values', { 0: MAX_AI_AGENTS_FILTER }));
        return;
      }
      setFilterRaw(key, values, exclude);
    },
    [setFilterRaw, t]
  );

  const filterDefinitions = useMemo(
    (): FilterDefinition[] => [
      {
        id: 'status',
        label: t('common.status'),
        icon: LightbulbIcon,
        type: 'radio',
        options: CAMPAIGN_STATUSES.map((status) => ({
          label: t(getCampaignStatusLabelKey(status)),
          value: status,
        })),
      },
      {
        id: 'channels',
        label: t('common.channels'),
        icon: BlocksIcon,
        type: 'checkbox',
        options: CHANNEL_OPTIONS,
      },
      {
        id: 'ai_agent',
        label: t('campaigns.ai_agent_handling'),
        icon: ZapIcon,
        type: 'checkbox',
        options: aiAgentOptions,
      },
    ],
    [t, aiAgentOptions]
  );

  const statusFilter = useMemo(() => getFilterValues(filters, 'status')[0], [filters]);
  const channelsFilter = useMemo(() => {
    const values = getFilterValues(filters, 'channels');
    return values.length > 0 ? values : undefined;
  }, [filters]);
  const aiAgentsFilter = useMemo(() => {
    const values = getFilterValues(filters, 'ai_agent');
    return values.length > 0 ? values : undefined;
  }, [filters]);

  const filterBadges = useMemo(
    () => buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter }),
    [filterDefinitions, filters, setFilter, removeFilter]
  );

  const {
    campaigns,
    flatCampaigns,
    listStatus,
    fetchNextPage,
    hasNextPage: serverHasNextPage,
    totalRows,
    isFetchingNextPage,
  } = useCampaigns(
    undefined,
    deferredSearchFilter,
    undefined,
    sortParam,
    statusFilter,
    channelsFilter,
    aiAgentsFilter
  );

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.WRITE)
  );

  // State for delete modal
  const [deleteCampaignId, setDeleteCampaignId] = useState<string | null>(null);
  const [stopCampaignId, setStopCampaignId] = useState<string | null>(null);

  // Use the server pagination hook to manage pagination state
  const {
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    currentPageData: currentPageCampaigns,
  } = useServerPagination(campaigns, fetchNextPage, {
    dataSelector: (page) => page?.campaigns || [],
    resetDependencies: [
      deferredSearchFilter,
      sortParam,
      statusFilter,
      channelsFilter,
      aiAgentsFilter,
    ],
    externalIsFetching: isFetchingNextPage,
    serverHasNextPage,
  });

  // Bulk selection is scoped to what is on screen; this lets the table drop it as soon as the
  // query changes, without waiting for the (keepPreviousData) refetch to swap the rows.
  const selectionResetKey = useMemo(
    () =>
      [
        deferredSearchFilter,
        sortParam ?? '',
        statusFilter ?? '',
        (channelsFilter ?? []).join(','),
        (aiAgentsFilter ?? []).join(','),
      ].join('|'),
    [deferredSearchFilter, sortParam, statusFilter, channelsFilter, aiAgentsFilter]
  );

  useSegments(PageName.CAMPAIGNS);

  // Clear campaign ID when on campaigns listing page
  useEffect(() => {
    dispatch(setCampaignId(null));
    dispatch(clearDraftCampaign());
  }, [dispatch]);

  /** Unified flow: find available name, create campaign, then navigate to setup */
  const handleCreateUnified = useCallback(
    (type: CampaignType) => {
      trackEvent(EventName.ClickCreateCampaign, { campaign_type: type });
      void createCampaign(type);
    },
    [createCampaign]
  );

  const isEmpty =
    flatCampaigns?.length === 0 &&
    listStatus === 'success' &&
    searchFilter.length === 0 &&
    activeFilterCount === 0;

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <GoalIcon size={16} className="text-muted-foreground" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal text-foreground">
                      {t('common.campaigns')}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        actions={
          <PageSubHeaderActions>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={!canWrite} size="sm">
                  <PlusIcon size={16} />
                  {t('common.create')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[272px]">
                <DropdownMenuItem
                  onClick={() => handleCreateUnified('marketing')}
                  className="flex flex-col items-start gap-1 rounded-md p-3"
                >
                  <span className="text-sm leading-none font-medium text-foreground">
                    {t('campaigns.outreach')}
                  </span>
                  <span className="text-sm leading-5 text-muted-foreground">
                    {t('campaigns.outreachDropdownDescription')}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleCreateUnified('payment_recovery')}
                  className="flex flex-col items-start gap-1 rounded-md p-3"
                >
                  <span className="text-sm leading-none font-medium text-foreground">
                    {t('campaigns.paymentRecovery')}
                  </span>
                  <span className="text-sm leading-5 text-muted-foreground">
                    {t('campaigns.paymentRecoveryDropdownDescription')}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </PageSubHeaderActions>
        }
      />

      <PageContentWrapper readOnly={!canWrite} noGutters>
        <div className={cn(!isEmpty && TABLE_LAYOUT_FILL_CLASSES)}>
          {isEmpty ? (
            <EmptyCampaignsPage />
          ) : (
            <CampaignsTable
              data={currentPageCampaigns}
              fetchNextPage={goToNextPage}
              hasNextPage={hasNextPage}
              currentPageIndex={currentPageIndex}
              setCurrentPageIndex={setCurrentPageIndex}
              rowCount={totalRows}
              onDeleteClick={setDeleteCampaignId}
              onStopClick={setStopCampaignId}
              isLoading={listStatus === 'pending'}
              isFetchingNextPage={isFetchingNextPage}
              sorting={sorting}
              onSortingChange={setSorting}
              selectionResetKey={selectionResetKey}
              renderToolbar={(selectionActions) => (
                <CampaignsToolbar
                  onSearchChange={setSearchFilter}
                  filterDefinitions={filterDefinitions}
                  filters={filters}
                  setFilter={setFilter}
                  removeFilter={removeFilter}
                  resetFilters={resetFilters}
                  activeFilterCount={activeFilterCount}
                  filterBadges={filterBadges}
                  openPillId={openPillId}
                  setOpenPillId={setOpenPillId}
                  selectionActions={selectionActions}
                />
              )}
            />
          )}
        </div>
      </PageContentWrapper>

      {/* Stop Campaign Modal */}
      <StopCampaignModal
        modalCampaignId={stopCampaignId}
        setModalCampaignId={setStopCampaignId}
        filter={deferredSearchFilter}
      />

      {/* Delete Campaign Modal */}
      <DeleteCampaignModal
        modalCampaignId={deleteCampaignId}
        setModalCampaignId={setDeleteCampaignId}
        filter={deferredSearchFilter}
      />
    </>
  );
};
